import { CostRuleKind, FscRuleKind, PayContractKind, Verdict } from "../enums.js";
import { COST_PROFILE_DEFAULTS } from "../entities.js";
import { roundCents } from "../money.js";
/**
 * Pure pay engine. No I/O, no Date.now(), no globals.
 * lease_pct math MUST match blueprint section 3.
 */
export function analyze(input) {
    const target = input.targetCentsPerMile ?? COST_PROFILE_DEFAULTS.targetCentsPerMile;
    const floor = input.floorCentsPerMile ?? COST_PROFILE_DEFAULTS.floorCentsPerMile;
    const L = Math.max(0, input.linehaulCents);
    const LM = Math.max(0, input.loadedMiles);
    const DM = Math.max(0, input.deadheadMiles);
    const acc = Math.max(0, input.accessorialsCents);
    const fscPerMile = input.fscRule.kind === FscRuleKind.fixed_per_mile
        ? input.fscRule.centsPerMile
        : Math.max(0, input.fscCentsPerMile);
    const fsc = LM > 0 ? roundCents(fscPerMile * LM) : 0;
    const pulled = fsc + acc;
    const overRate = pulled > L;
    const feeBase = overRate ? 0 : Math.max(0, L - pulled);
    const kind = input.kind ?? PayContractKind.lease_pct;
    let cut;
    let keep;
    if (kind === PayContractKind.per_mile) {
        cut = 0;
        keep = roundCents((input.perLoadedMileCents ?? 0) * LM);
    }
    else if (kind === PayContractKind.flat) {
        cut = 0;
        keep = Math.max(0, input.flatCents ?? 0);
    }
    else {
        // lease_pct
        cut = overRate ? 0 : roundCents((input.cutBps / 10000) * feeBase);
        keep = overRate ? L : feeBase - cut + pulled;
    }
    const TM = LM + DM;
    const gallons = input.mpg > 0 ? TM / input.mpg : 0;
    const fuel = roundCents(gallons * input.dieselCentsPerGallon);
    let other;
    let truckPay;
    let cost;
    if (input.costRule.kind === CostRuleKind.weekly_truck) {
        other = 0;
        const minutes = (input.routeMinutes ?? 0) + (input.routeDeadheadMinutes ?? 0);
        truckPay =
            minutes > 0
                ? roundCents((input.costRule.weeklyCents * minutes) / 10080)
                : 0;
        cost = fuel + truckPay;
    }
    else {
        // per_mile
        other = roundCents(TM * input.otherCentsPerMile);
        truckPay = 0;
        cost = fuel + other;
    }
    const profit = keep - cost;
    const keepPerLoadedMile = LM > 0 ? keep / LM : null;
    const keepPerTotalMile = TM > 0 ? keep / TM : null;
    // Verdict "ppm" is profit per loaded mile (see V5: ppm 53.933 = profit/LM).
    const profitPerLoadedMile = LM > 0 ? profit / LM : null;
    const verdict = computeVerdict({
        L,
        LM,
        cost,
        profit,
        ppm: profitPerLoadedMile,
        target,
        floor,
    });
    return {
        linehaulCents: L,
        fscCents: fsc,
        accessorialsCents: acc,
        pulledCents: pulled,
        overRate,
        feeBaseCents: feeBase,
        cutCents: cut,
        keepCents: keep,
        loadedMiles: LM,
        deadheadMiles: DM,
        totalMiles: TM,
        gallons,
        fuelCents: fuel,
        otherCents: other,
        truckPayCents: truckPay,
        costCents: cost,
        profitCents: profit,
        keepPerLoadedMile,
        keepPerTotalMile,
        profitPerLoadedMile,
        verdict,
        targetCentsPerMile: target,
        floorCentsPerMile: floor,
    };
}
function computeVerdict(args) {
    const { L, LM, cost, profit, ppm, target, floor } = args;
    if (L <= 0 && LM <= 0 && cost <= 0) {
        return Verdict.enter_rate;
    }
    if (LM <= 0 || cost <= 0) {
        return Verdict.you_keep;
    }
    // ppm is non-null when LM > 0
    const rate = ppm ?? 0;
    if (rate >= target && rate >= floor) {
        return Verdict.take_it;
    }
    if (rate >= floor) {
        return Verdict.thin;
    }
    if (profit >= 0) {
        return Verdict.barely;
    }
    return Verdict.pass;
}
//# sourceMappingURL=analyze.js.map